/* eslint-disable camelcase */
import { LOCATION_CHANGE } from 'connected-react-router';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { REHYDRATE } from 'redux-persist';
import { EMPTY, from, merge, of, timer } from 'rxjs';
import {
  exhaustMap,
  filter,
  find,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';

import {
  createLoginViaQrToken,
  getLoginViaQrTokenDetails,
  qrLoginAuthorizedByUser,
  setLoginViaQrTokenStatus,
  setUserAuthorizedByLoginViaBiometrics,
  setUserAuthorizedByLoginViaQr,
} from 'actions/auth';
import { getAccountToken, isAuthenticated, setUnauthorized } from 'actions/user';
import AUTH_ACTION_TYPES, {
  LoginViaBiometricsActionTypes,
  LoginViaQrActionTypes,
} from 'actionTypes/auth';
import USER, { UNAUTHORIZED } from 'actionTypes/user';
import { postMessageToMobileApp } from 'components/app/helpers';
import { ERROR_CODES, IS_RN_HOME_SCREEN } from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS } from 'constants/enums';
import { identifyUser, trackMixpanelEvent } from 'helpers/mixpanel-init';
import { equals, gt, has, hasPath } from 'helpers/ramda';
import { addLogsToSentry, setLoggedInInSentry } from 'helpers/sentry';
import { isAuthTokenExpired } from 'helpers/user';
import { userSelector, gRecaptchaSelector } from 'selectors/otherSelectors';
import { LoginViaQrTokenStatus, QrAuthStatus } from 'types/Auth';
import IState, { CustomAction } from 'types/Istore';

const MINUTE_IN_MS = 1000;
const POLLING_FOR_TOKEN_DETAILS_INTERVAL = MINUTE_IN_MS * 5;
const TOKEN_EXPIRY_CHECK_INTERVAL = MINUTE_IN_MS * 60; // Check every minute

// Measure the effect of this function
const getDeviceName = async (): Promise<string> => {
  const UaParser = (await import('ua-parser-js')).default;
  const parser = new UaParser();
  const { browser, os } = parser.getResult();

  return `${browser?.name} (${os?.name})`;
};

const shouldStopPollingEffects = (action$: CustomAction) => {
  return merge(
    action$.ofType(LoginViaQrActionTypes.LOGIN_AUTHORIZED),
    action$.ofType(LOCATION_CHANGE),
    2
  );
};

// #region Login Via QR
const openLoginViaQrMobileScannerEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(LoginViaQrActionTypes.OPEN_SCANNER),
    map(() => {
      trackMixpanelEvent('Scan icon clicked', {});

      return postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.OPEN_QR_LOGIN_SCANNER);
    })
  );

const createLoginViaQrTokenEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(LoginViaQrActionTypes.REFRESH_TOKEN),
    filter(({ payload }: CustomAction) => has('recaptcha', payload)),
    switchMap(({ payload }) =>
      from(getDeviceName()).pipe(
        map((deviceName: string) => createLoginViaQrToken(deviceName, payload.recaptcha))
      )
    )
  );

const invalidCaptchaEpic = (action$: CustomAction, state$: StateObservable<IState>) =>
  action$.pipe(
    ofType(USER.LOGIN.FAIL, AUTH_ACTION_TYPES[LoginViaQrActionTypes.CREATE_TOKEN]?.FAIL),
    tap((res: any) => {
      const errorResp = res?.error?.response?.body?.error;
      if (errorResp?.code === ERROR_CODES.INVALID_CAPTCHA) {
        const { hidden, initialised } = gRecaptchaSelector(state$.value);
        const singletonShown = !hidden && initialised;
        addLogsToSentry(
          null,
          `Invalid Captcha - ${res.type}. singletonShown: ${singletonShown}`,
          {
            extra: { error: errorResp, source: 'invalidCaptchaEpic' },
          },
          { recaptchaError: 'invalid captcha' }
        );
      }
    }),
    mergeMap(() => EMPTY)
  );

const startTokenExpiryCountdownEpic = (
  action$: CustomAction,
  state$: StateObservable<IState>
) =>
  action$.pipe(
    ofType(AUTH_ACTION_TYPES[LoginViaQrActionTypes.CREATE_TOKEN]?.SUCCESS),
    filter(({ result }: CustomAction) => hasPath(['result', 'expires_at'], result)),
    switchMap(({ result }: CustomAction) => {
      const expiryTimeInMs = result.result.expires_at * MINUTE_IN_MS;

      return timer(0, MINUTE_IN_MS).pipe(
        find(() => gt(Date.now(), expiryTimeInMs)),
        takeUntil(shouldStopPollingEffects(action$)),
        takeWhile(() =>
          equals(state$.value.auth.login.viaQr.status, LoginViaQrTokenStatus.LOADED)
        ),
        map(() => setLoginViaQrTokenStatus(LoginViaQrTokenStatus.SHOULD_REFRESH))
      );
    })
  );

const startPollingForTokenDetailsEpic = (
  action$: CustomAction,
  state$: StateObservable<IState>
) =>
  action$.pipe(
    ofType(AUTH_ACTION_TYPES[LoginViaQrActionTypes.CREATE_TOKEN]?.SUCCESS),
    filter(({ result }: CustomAction) => hasPath(['result', 'token'], result)),
    switchMap(({ result }: CustomAction) =>
      timer(0, POLLING_FOR_TOKEN_DETAILS_INTERVAL).pipe(
        map(() => getLoginViaQrTokenDetails(result.result.token)),
        takeUntil(shouldStopPollingEffects(action$)),
        takeWhile(() =>
          equals(state$.value.auth.login.viaQr.status, LoginViaQrTokenStatus.LOADED)
        )
      )
    )
  );

const checkTokenAuthorizationByUserEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(AUTH_ACTION_TYPES[LoginViaQrActionTypes.GET_TOKEN_DETAILS]?.SUCCESS),
    mergeMap((action: CustomAction) => {
      const { result } = action;

      if (equals(result.result.status, QrAuthStatus.APPROVED)) {
        const { user } = result.result;
        identifyUser(String(user.id));

        trackMixpanelEvent('Authorize clicked', {});

        return of(qrLoginAuthorizedByUser({ user }));
      }

      return EMPTY;
    })
  );

const setUserOnLoginViaQrAuthorizationEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(LoginViaQrActionTypes.LOGIN_AUTHORIZED),
    filter(({ payload }: CustomAction) => has('user', payload)),
    map(({ payload }: CustomAction) => {
      trackMixpanelEvent('Login is successful through app scan', {
        page_name: 'Sign-in',
        $email: payload.user.email,
        is_loggedin: true,
      });

      return setUserAuthorizedByLoginViaQr({ user: payload.user });
    })
  );
// #endregion

// #region Login Via Biometrics
const setUserOnBiometricsAuthorizationEpic = (action$: CustomAction) =>
  action$.pipe(
    ofType(LoginViaBiometricsActionTypes.LOGIN_AUTHORIZED),
    filter(({ payload }: CustomAction) => has('user', payload)),
    map(({ payload }: CustomAction) => {
      return setUserAuthorizedByLoginViaBiometrics({ user: payload.user });
    })
  );

const switchToSubAccountOnBiometricsAuthorizationEpic = (
  action$: CustomAction,
  state$
) => {
  return action$.pipe(
    ofType(LoginViaBiometricsActionTypes.SWITCH_TO_SUBACCOUNT),
    filter(({ payload }: CustomAction) => has('userId', payload)),
    exhaustMap(({ payload }: CustomAction) =>
      state$.pipe(
        filter(({ user }) => Boolean(user?.token)),
        take(1),
        map(() => {
          if (IS_RN_HOME_SCREEN()) {
            postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT);
          }
          return getAccountToken({ sub_account_user_id: payload.userId });
        })
      )
    )
  );
};
// #endregion

// #region Token Expiry Management

const dateNow = () => Date.now() / 1000;

const checkTokenExpiryOnRehydrateEpic = (
  action$: CustomAction,
  state$: StateObservable<IState>
) =>
  action$.pipe(
    ofType(REHYDRATE),
    filter(() => isAuthenticated(state$.value.user)),
    filter(() => {
      const user = userSelector(state$.value);
      const isExpired = isAuthTokenExpired(user);
      // Update Sentry user context on page reload for authenticated users with valid tokens
      if (!isExpired) {
        setLoggedInInSentry(user);
      }

      return isExpired;
    }),
    mergeMap(() => {
      const { expires_at } = state$.value.user;
      const currentTime = dateNow();

      trackMixpanelEvent('Auto logout - Token expired on rehydrate', {
        expires_at,
        current_time: currentTime,
      });
      return of(setUnauthorized({}));
    })
  );

const monitorTokenExpiryDuringSessionEpic = (
  action$: CustomAction,
  state$: StateObservable<IState>
) =>
  action$.pipe(
    ofType<any>(
      USER.LOGIN.SUCCESS,
      USER.LOGINMFA.SUCCESS,
      USER.LOGIN_EMAIL_VERIFICATION.SUCCESS,
      USER.PHONE_OTP_VERIFICATION.SUCCESS,
      USER.REFRESH_TOKEN.SUCCESS,
      LoginViaQrActionTypes.SET_AUTHORIZED_USER,
      LoginViaBiometricsActionTypes.SET_AUTHORIZED_USER,
      REHYDRATE
    ),
    filter((action: CustomAction) => {
      // For REHYDRATE, only proceed if user is authenticated and token not already expired
      if (action.type === REHYDRATE) {
        return (
          isAuthenticated(state$.value.user) && !isAuthTokenExpired(state$.value.user)
        );
      }
      return true;
    }),
    switchMap(() => {
      // Start checking immediately (0ms delay), then every 60 seconds
      return timer(0, TOKEN_EXPIRY_CHECK_INTERVAL).pipe(
        filter(() => isAuthenticated(state$.value.user)),
        takeWhile(() => isAuthenticated(state$.value.user)),
        takeUntil(action$.ofType(USER.LOGOUT.SUCCESS, UNAUTHORIZED)),
        mergeMap(() => {
          const { expires_at } = state$.value.user;
          const currentTime = dateNow();
          const isExpired = isAuthTokenExpired(state$.value.user);

          if (isExpired) {
            trackMixpanelEvent('Auto logout - Token expired during session', {
              expires_at,
              current_time: currentTime,
            });
            return of(setUnauthorized({}));
          }
          return EMPTY;
        })
      );
    })
  );
// #endregion

export default combineEpics(
  // #region Login Via QR
  openLoginViaQrMobileScannerEpic,
  createLoginViaQrTokenEpic,
  invalidCaptchaEpic,
  startTokenExpiryCountdownEpic,
  startPollingForTokenDetailsEpic,
  checkTokenAuthorizationByUserEpic,
  setUserOnLoginViaQrAuthorizationEpic,
  // #endregion

  // #region Login Via Biometrics
  setUserOnBiometricsAuthorizationEpic,
  switchToSubAccountOnBiometricsAuthorizationEpic,
  // #endregion

  // #region Token Expiry Management
  checkTokenExpiryOnRehydrateEpic,
  monitorTokenExpiryDuringSessionEpic
  // #endregion
);
